"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosOrderCreateInputSchema = exports.APosOrderSchema = exports.APosOrderDiscountSchema = exports.APosOrderPaymentSchema = exports.APosOrderItemSchema = exports.APosPaymentStatusSchema = exports.APosPaymentMethodSchema = exports.APosOrderMetaSchema = exports.APosOrderPriorityLevelSchema = exports.APosSubstitutionPolicySchema = exports.APosOrderChannelSchema = exports.APosOrderTypeSchema = exports.APosOrderStatusSchema = void 0;
const zod_1 = require("zod");
const kitchen_schema_1 = require("./kitchen.schema");
const timestamps_schema_1 = require("../../common/timestamps.schema");
const id_schema_1 = require("../../common/id.schema");
const location_schema_1 = require("./location.schema");
exports.APosOrderStatusSchema = zod_1.z.enum([
    // Legacy retail statuses — DO NOT remove (backward compatibility)
    'pending', 'accepted', 'rejected', 'cancelled',
    'picking', 'packed', 'ready',
    'out_for_delivery', 'picked_up', 'delivered', 'completed',
    // Phase F — online/marketplace lifecycle
    'pending_accept', 'preparing', 'handed_over', 'delivering',
]);
exports.APosOrderTypeSchema = zod_1.z.enum([
    // Legacy — DO NOT remove
    'retail', 'pickup', 'delivery',
    // Phase F — online/marketplace
    'online_pickup', 'online_delivery', 'marketplace_pickup', 'marketplace_delivery',
]);
exports.APosOrderChannelSchema = zod_1.z.enum(['pos', 'online', 'marketplace']);
exports.APosSubstitutionPolicySchema = zod_1.z.enum(['allow', 'refund_if_oos', 'contact_me']);
exports.APosOrderPriorityLevelSchema = zod_1.z.enum(['normal', 'rush']);
/**
 * OrderMeta — the blob `complete-sale` forwards into the SQS message and
 * `createOrder` destructures to stamp online/marketplace orders. For a
 * normal POS sale, `orderMeta` is absent and createOrder falls back to
 * retail/pos/completed (legacy behaviour preserved).
 */
exports.APosOrderMetaSchema = zod_1.z.object({
    orderType: exports.APosOrderTypeSchema,
    channel: exports.APosOrderChannelSchema,
    fulfillmentMethod: zod_1.z.enum(['pickup', 'delivery']).optional(),
    cartId: id_schema_1.EntityIdSchema.optional(),
    marketplaceId: id_schema_1.EntityIdSchema.optional(),
    sellerTenantId: id_schema_1.TenantIdSchema.optional(),
    substitutionPolicy: exports.APosSubstitutionPolicySchema.optional(),
    priorityLevel: exports.APosOrderPriorityLevelSchema.optional(),
    // NMI auth handle from Phase E checkout/authorize — captured at fulfill.
    authTransactionId: zod_1.z.string().optional(),
    authAmount: zod_1.z.number().nonnegative().optional(),
    estimatedPickupMinutes: zod_1.z.number().int().nonnegative().optional(),
    estimatedDeliveryWindow: zod_1.z.string().optional(),
    customerNote: zod_1.z.string().max(2000).optional(),
});
exports.APosPaymentMethodSchema = zod_1.z.enum(['cash', 'card', 'store_credit']);
exports.APosPaymentStatusSchema = zod_1.z.enum(['pending', 'completed', 'failed', 'refunded']);
exports.APosOrderItemSchema = zod_1.z.object({
    lineId: id_schema_1.EntityIdSchema,
    variantId: id_schema_1.EntityIdSchema,
    displayName: zod_1.z.string().min(1),
    sku: zod_1.z.string().optional(),
    quantity: zod_1.z.number().int().positive(),
    unitPrice: zod_1.z.number().nonnegative(),
    grossTotal: zod_1.z.number().nonnegative(),
    discount: zod_1.z.number().nonnegative().optional(),
    discountReason: zod_1.z.string().optional(),
    discountSummary: zod_1.z.array(zod_1.z.object({
        source: zod_1.z.enum(['manual', 'loyalty', 'promotion', 'employee', 'cash']),
        sourceName: zod_1.z.string(),
        amount: zod_1.z.number(),
    })).optional(),
    lineTotal: zod_1.z.number().nonnegative(),
    costPrice: zod_1.z.number().nonnegative().optional(),
    commissionId: id_schema_1.EntityIdSchema.optional(),
    commissionName: zod_1.z.string().optional(),
    commissionAmount: zod_1.z.number().nonnegative().optional(),
    isAgeRestricted: zod_1.z.boolean().optional(),
    // Kitchen & Menu (all optional, additive)
    productTypeId: zod_1.z.string().optional(),
    recipeId: zod_1.z.string().optional(),
    recipeSizeId: zod_1.z.string().optional(),
    modifiers: zod_1.z.array(kitchen_schema_1.SaleLineModifierSchema).optional(),
    notes: zod_1.z.string().max(500).optional(),
    stationId: zod_1.z.string().optional(),
    // Restaurant, reserved — not built yet
    tableId: zod_1.z.string().optional(),
    seatNo: zod_1.z.number().int().positive().optional(),
    course: zod_1.z.number().int().positive().optional(),
});
exports.APosOrderPaymentSchema = zod_1.z.object({
    paymentId: id_schema_1.EntityIdSchema,
    method: exports.APosPaymentMethodSchema,
    amount: zod_1.z.number().nonnegative(),
    stripePaymentIntentId: zod_1.z.string().optional(),
    cardBrand: zod_1.z.string().optional(),
    cardLast4: zod_1.z.string().length(4).optional(),
    status: exports.APosPaymentStatusSchema,
    processedAt: zod_1.z.number(),
});
exports.APosOrderDiscountSchema = zod_1.z.object({
    // Existing fields — DO NOT remove or rename
    type: zod_1.z.enum(['line', 'cart', 'coupon', 'promotion']),
    promotionId: id_schema_1.EntityIdSchema.optional(),
    couponCode: zod_1.z.string().optional(),
    description: zod_1.z.string(),
    amount: zod_1.z.number().nonnegative(),
    // New attribution fields (all optional for backward compatibility)
    source: zod_1.z.enum(['manual', 'loyalty', 'promotion', 'employee', 'cash']).optional(),
    scope: zod_1.z.enum(['line', 'cart']).optional(),
    sourceName: zod_1.z.string().optional(),
    lineId: zod_1.z.string().optional(),
    stackable: zod_1.z.boolean().optional(),
    reason: zod_1.z.string().optional(),
    appliedBy: zod_1.z.object({
        employeeId: zod_1.z.string(),
        employeeName: zod_1.z.string(),
    }).optional(),
    loyaltyPointsRedeemed: zod_1.z.number().int().nonnegative().optional(),
    promotionName: zod_1.z.string().optional(),
});
exports.APosOrderSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    orderId: id_schema_1.EntityIdSchema,
    orderNumber: zod_1.z.string().min(1),
    locationId: id_schema_1.EntityIdSchema,
    type: exports.APosOrderTypeSchema,
    channel: exports.APosOrderChannelSchema,
    status: exports.APosOrderStatusSchema.default('pending'),
    customerId: id_schema_1.EntityIdSchema.optional(),
    customerName: zod_1.z.string().optional(),
    customerPhone: zod_1.z.string().optional(),
    customerEmail: zod_1.z.string().email().optional(),
    items: zod_1.z.array(exports.APosOrderItemSchema).min(1),
    subtotal: zod_1.z.number().nonnegative(),
    discounts: zod_1.z.array(exports.APosOrderDiscountSchema).default([]),
    totalDiscount: zod_1.z.number().nonnegative().default(0),
    deliveryFee: zod_1.z.number().nonnegative().optional(),
    tax: zod_1.z.number().nonnegative(),
    total: zod_1.z.number().nonnegative(),
    payments: zod_1.z.array(exports.APosOrderPaymentSchema).default([]),
    paymentStatus: exports.APosPaymentStatusSchema.default('pending'),
    deliveryAddress: location_schema_1.APosAddressSchema.optional(),
    pickupCode: zod_1.z.string().optional(),
    fulfilledAt: zod_1.z.number().optional(),
    fulfilledBy: id_schema_1.EntityIdSchema.optional(),
    staffId: id_schema_1.EntityIdSchema,
    staffName: zod_1.z.string().optional(),
    loyaltyPointsEarned: zod_1.z.number().int().nonnegative().optional(),
    loyaltyPointsRedeemed: zod_1.z.number().int().nonnegative().optional(),
    commissionTotal: zod_1.z.number().nonnegative().optional(),
    commissionEmployeeId: id_schema_1.EntityIdSchema.optional(),
    commissions: zod_1.z.array(zod_1.z.object({
        lineId: id_schema_1.EntityIdSchema,
        variantId: id_schema_1.EntityIdSchema,
        displayName: zod_1.z.string(),
        quantity: zod_1.z.number().int().positive(),
        lineTotalPaid: zod_1.z.number().nonnegative(),
        commissionId: id_schema_1.EntityIdSchema,
        commissionName: zod_1.z.string(),
        commissionType: zod_1.z.enum(['flat_per_unit', 'percent_of_sale']),
        commissionAmount: zod_1.z.number().nonnegative(),
    })).optional(),
    ageVerified: zod_1.z.boolean().optional(),
    ageVerifiedAt: zod_1.z.number().optional(),
    notes: zod_1.z.string().max(2000).optional(),
    // Phase F — online order fields (all optional → backward-compat)
    orderType: exports.APosOrderTypeSchema.optional(),
    fulfillmentMethod: zod_1.z.enum(['pickup', 'delivery']).optional(),
    marketplaceId: id_schema_1.EntityIdSchema.optional(),
    sellerTenantId: id_schema_1.TenantIdSchema.optional(),
    substitutionPolicy: exports.APosSubstitutionPolicySchema.optional(),
    priorityLevel: exports.APosOrderPriorityLevelSchema.optional(),
    authTransactionId: zod_1.z.string().optional(),
    authAmount: zod_1.z.number().nonnegative().optional(),
    capturedAmount: zod_1.z.number().nonnegative().optional(),
    captureTransactionId: zod_1.z.string().optional(),
    customerNote: zod_1.z.string().max(2000).optional(),
    receivedAt: zod_1.z.number().optional(),
    acceptedAt: zod_1.z.number().optional(),
    // Checkout-loyalty (additive). NOTE: `loyaltyPointsEarned` already exists above
    // (line ~150, int/nonnegative/optional) — not re-declared here to avoid a
    // duplicate object key.
    loyaltyAccruedAt: zod_1.z.string().optional(),
    // Kitchen & Menu — phone / delivery / dine-in details (additive)
    fulfillment: kitchen_schema_1.OrderFulfillmentSchema.optional(),
    kitchenStatus: zod_1.z.enum(['queued', 'making', 'oven', 'ready', 'bumped']).optional(),
    deliveryRunId: zod_1.z.string().optional(),
}).merge(timestamps_schema_1.TimestampsSchema);
exports.APosOrderCreateInputSchema = zod_1.z.object({
    locationId: id_schema_1.EntityIdSchema,
    type: exports.APosOrderTypeSchema,
    channel: exports.APosOrderChannelSchema,
    customerId: id_schema_1.EntityIdSchema.optional(),
    items: zod_1.z.array(zod_1.z.object({
        variantId: id_schema_1.EntityIdSchema,
        displayName: zod_1.z.string().min(1),
        sku: zod_1.z.string().optional(),
        quantity: zod_1.z.number().int().positive(),
        unitPrice: zod_1.z.number().nonnegative(),
    })).min(1),
    deliveryAddress: location_schema_1.APosAddressSchema.optional(),
    notes: zod_1.z.string().max(2000).optional(),
});
//# sourceMappingURL=order.schema.js.map